import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { ArrowLeft, Eye } from '@phosphor-icons/react';
import api from '../../../utils/api';
import ProposalCraftEditor, { MetaFieldHint } from './ProposalCraftEditor';
import {
  ProposalLineItemModal,
  ProposalBillingScheduleModal,
  ProposalTermsModal
} from './ProposalPricingDialogs';
import { filterLayoutBlocks, normaliseLayoutBlocks } from '../../../utils/proposalCraftUtils';
import { buildProposalPreviewPayload, openProposalSupplierPreview } from '../../../utils/openProposalPreview';
import { useBusiness } from '../../../context/BusinessContext';
import { usePlatformAlerts } from '../../../context/PlatformAlertsContext';

function TemplateForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { selectedBusiness } = useBusiness();
  const { showError, showSuccess } = usePlatformAlerts();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    templateName: '',
    termsText: '',
    currency: 'GBP',
    lineItems: [],
    billingSchedule: [{ label: 'Deposit', amountType: 'percentage', value: 30, dueType: 'immediate', dueValue: 0 }],
    content: { blocks: [] },
    additionalInformationFields: []
  });
  const craftEditorRef = useRef(null);
  const [lineItemModal, setLineItemModal] = useState({ open: false, index: null });
  const [scheduleModal, setScheduleModal] = useState({ open: false, index: null });
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const businessId = selectedBusiness?.business?._id;

  const fetchProducts = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await api.get(`/api/products/business/${businessId}`);
      setProducts(res.data || []);
    } catch {
      setProducts([]);
    }
  }, [businessId]);

  const fetchTemplate = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await api.get(`/api/agreements/${id}`);
      const t = res.data;
      const loadedBlocks = t.content?.blocks || [];
      setFormData({
        templateName: t.templateName || '',
        termsText: t.termsText || '',
        currency: t.currency || 'GBP',
        lineItems: (t.lineItems || []).length > 0
          ? t.lineItems.map((i) => ({
              ...(i._id ? { _id: i._id } : {}),
              productId: i.productId || '',
              description: i.description || '',
              quantity: i.quantity || 1,
              unitPrice: i.unitPrice ?? '',
              taxPercentage: i.taxPercentage ?? 0,
              priceIncludesTax: Boolean(i.priceIncludesTax),
              isOptional: !!i.isOptional
            }))
          : [],
        billingSchedule: (t.billingSchedule || []).length > 0
          ? t.billingSchedule.map((s) => ({
              label: s.label || '',
              amountType: s.amountType || 'percentage',
              value: s.value ?? 30,
              dueType: s.dueType || 'immediate',
              dueValue: s.dueValue ?? 0
            }))
          : [{ label: 'Deposit', amountType: 'percentage', value: 30, dueType: 'immediate', dueValue: 0 }],
        content: loadedBlocks.length
          ? {
              blocks: normaliseLayoutBlocks(loadedBlocks)
            }
          : { blocks: [] },
        additionalInformationFields: t.additionalInformationFields || []
      });
    } catch {
      showError('Failed to load template');
    } finally {
      setLoading(false);
    }
  }, [id, showError]);

  useEffect(() => {
    if (businessId) {
      fetchProducts();
      if (isEdit) fetchTemplate();
    }
  }, [businessId, isEdit, fetchProducts, fetchTemplate]);

  useEffect(() => {
    const message = location.state?.savedMessage;
    if (!message) return;
    showSuccess(message);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.pathname, location.search, location.state, navigate, showSuccess]);

  const handleSaveLineItem = (record) => {
    setFormData((prev) => {
      const next = [...prev.lineItems];
      const row = {
        ...record,
        unitPrice: record.unitPrice === '' || record.unitPrice === null ? '' : String(record.unitPrice)
      };
      const idx = lineItemModal.index;
      if (idx === null) {
        next.push(row);
      } else {
        next[idx] = row;
      }
      return { ...prev, lineItems: next };
    });
  };

  const handleSaveSchedule = (record) => {
    setFormData((prev) => {
      const next = [...prev.billingSchedule];
      const idx = scheduleModal.index;
      if (idx === null) {
        next.push(record);
      } else {
        next[idx] = record;
      }
      return { ...prev, billingSchedule: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = formData.lineItems.filter((i) => (i.description || '').trim() && (i.unitPrice !== '' && i.unitPrice !== null && !isNaN(parseFloat(i.unitPrice))));
    if (validItems.length === 0) {
      showError('Add at least one line item with description and price');
      return;
    }

    const blocksFromEditor = craftEditorRef.current?.getBlocks?.();
    const contentToSave = formData.content || { blocks: [] };
    const raw = Array.isArray(blocksFromEditor) ? blocksFromEditor : (contentToSave.blocks || []);
    const blocks = filterLayoutBlocks(raw);

    const payload = {
      templateName: formData.templateName.trim() || undefined,
      message: '',
      termsText: formData.termsText.trim(),
      currency: formData.currency,
      lineItems: validItems.map((i) => ({
        ...(i._id ? { _id: i._id } : {}),
        productId: i.productId || undefined,
        description: i.description.trim(),
        quantity: Number(i.quantity) || 1,
        unitPrice: parseFloat(i.unitPrice) || 0,
        taxPercentage: parseFloat(i.taxPercentage) || 0,
        priceIncludesTax: Boolean(i.priceIncludesTax),
        isOptional: !!i.isOptional
      })),
      billingSchedule: formData.billingSchedule
        .filter((s) => (s.label || '').trim())
        .map((s) => ({
          label: s.label.trim(),
          amountType: s.amountType,
          value: parseFloat(s.value) || 0,
          dueType: s.dueType,
          dueValue: parseInt(s.dueValue, 10) || 0
        })),
      content: { blocks },
      additionalInformationFields: (formData.additionalInformationFields || []).map((f, index) => ({
        type: f.type === 'textarea' ? 'textarea' : 'text',
        label: (f.label || '').trim(),
        placeholder: (f.placeholder || '').trim() || undefined,
        order: typeof f.order === 'number' ? f.order : index
      }))
    };

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/api/agreements/${id}`, payload);
        showSuccess('Template saved successfully.');
      } else {
        const { data: created } = await api.post('/api/agreements/template', {
          businessId,
          ...payload
        });
        if (created?._id) {
          navigate(`/supplier/agreements/templates/${created._id}/edit`, {
            state: { savedMessage: 'Template saved successfully.' }
          });
          return;
        }
      }
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenPreview = () => {
    try {
      const payload = buildProposalPreviewPayload({
        kind: 'template',
        formData,
        craftEditorRef,
        businessName: selectedBusiness?.business?.name
      });
      openProposalSupplierPreview(payload);
    } catch (err) {
      showError(err?.message || 'Could not open preview.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 120px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Button type="button" startIcon={<ArrowLeft size={18} />} onClick={() => navigate('/supplier/agreements/templates')}>
            Back
          </Button>
          <TextField
            value={formData.templateName}
            onChange={(e) => setFormData((p) => ({ ...p, templateName: e.target.value }))}
            placeholder={isEdit ? 'Template name' : 'New template name'}
            variant="standard"
            fullWidth
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: { xs: '100%', sm: 480, md: 560 },
              '& .MuiInput-input': {
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MetaFieldHint title="Used in email and chat. Not shown on the published agreement page." sx={{ ml: 0 }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button type="button" variant="outlined" startIcon={<Eye size={18} />} onClick={handleOpenPreview}>
            Preview
          </Button>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : isEdit ? 'Save' : 'Create template'}
          </Button>
        </Box>
      </Box>

      <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pt: 0,
            px: 0,
            pb: { xs: 2, md: 3 }
          }}
        >
          <ProposalCraftEditor
            key={isEdit ? id : 'new'}
            blocks={formData.content?.blocks || []}
            onBlocksChange={(blocks) => setFormData((p) => ({ ...p, content: { blocks } }))}
            editorRef={craftEditorRef}
            showRecipientTab={false}
            showInvoicingTab={false}
            termsText={formData.termsText}
            lineItems={formData.lineItems}
            billingSchedule={formData.billingSchedule}
            currency={formData.currency}
            onAddLineItem={() => setLineItemModal({ open: true, index: null })}
            onEditLineItem={(idx) => setLineItemModal({ open: true, index: idx })}
            onRemoveLineItem={(idx) =>
              setFormData((prev) => ({
                ...prev,
                lineItems: prev.lineItems.filter((_, i) => i !== idx)
              }))
            }
            onAddSchedule={() => setScheduleModal({ open: true, index: null })}
            onEditSchedule={(idx) => setScheduleModal({ open: true, index: idx })}
            onRemoveSchedule={(idx) =>
              setFormData((prev) => ({
                ...prev,
                billingSchedule: prev.billingSchedule.filter((_, i) => i !== idx)
              }))
            }
            onEditTerms={() => setTermsModalOpen(true)}
            additionalInformationFields={formData.additionalInformationFields}
            onAdditionalInformationFieldsChange={(fields) =>
              setFormData((p) => ({ ...p, additionalInformationFields: fields }))
            }
          />
        </Box>
      </Box>

      <ProposalLineItemModal
        open={lineItemModal.open}
        onClose={() => setLineItemModal({ open: false, index: null })}
        initial={lineItemModal.index !== null ? formData.lineItems[lineItemModal.index] : null}
        onSave={handleSaveLineItem}
        products={products}
        title={lineItemModal.index === null ? 'Add line item' : 'Edit line item'}
      />
      <ProposalBillingScheduleModal
        open={scheduleModal.open}
        onClose={() => setScheduleModal({ open: false, index: null })}
        initial={scheduleModal.index !== null ? formData.billingSchedule[scheduleModal.index] : null}
        onSave={handleSaveSchedule}
        title={scheduleModal.index === null ? 'Add schedule item' : 'Edit schedule item'}
      />
      <ProposalTermsModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        value={formData.termsText}
        onSave={(text) => setFormData((p) => ({ ...p, termsText: text }))}
      />
    </form>
  );
}

export default TemplateForm;
